import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useInventory } from '../../../contexts/InventoryContext';
import LoadingSpinner from '../../Common/LoadingSpinner';
import PermissionGuard from '../../Common/PermissionGuard';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import CategoryForm from './CategoryForm';
import CategoryViewForm from './CategoryViewForm';

const CategoryManagement = () => {
  const {
    categories,
    loading,
    error,
    fetchCategories,
    deleteCategory,
    clearError
  } = useInventory();

  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('categoryManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewingCategory, setViewingCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    fetchCategories().catch(err => toast.error(err.response?.data?.message || err.message || 'Failed to load categories'));
  }, []);

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('categoryManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleLevelFilter = (e) => {
    setLevelFilter(e.target.value);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleViewCategory = (category) => {
    setViewingCategory(category);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDeleteCategory = async (category) => {
    try {
      const res = await deleteCategory(category._id);
      toast.success(res?.message || 'Category deleted successfully');
      setShowDeleteConfirm(null);
      await fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to delete category');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCategory(null);
    // Fetch fresh data after form success
    fetchCategories().catch(err => toast.error(err.response?.data?.message || err.message || 'Failed to load categories'));
  };

  // Filter categories
  const filteredCategories = categories.filter(category => {
    const matchesSearch = !searchTerm ||
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesLevel = levelFilter === 'all' || category.level === parseInt(levelFilter);

    return matchesSearch && matchesLevel;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // latest first

  // Pagination calculations
  const totalItems = filteredCategories.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCategories = filteredCategories.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, levelFilter]);

  const getCategoryIcon = (category) => {
    return category.code.substring(0, 2).toUpperCase();
  };

  const renderCategoryCard = (category) => {
    return (
      <div key={category._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-28 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                {getCategoryIcon(category)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {category.name}
                </h3>
                <p className="text-xs text-gray-500 truncate">{category.code}</p>
              </div>
            </div>

            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                onClick={() => handleViewCategory(category)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title="View details"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>

              <PermissionGuard permission="INVENTORY:CATEGORIES:UPDATE">
                <button
                  onClick={() => handleEditCategory(category)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                  title="Edit category"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="INVENTORY:CATEGORIES:DELETE">
                <button
                  onClick={() => {
                    clearError();
                    setShowDeleteConfirm(category);
                  }}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Delete category"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </PermissionGuard>
            </div>
          </div>

          {category.description && (
            <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
              {category.description}
            </p>
          )}

          <div className="flex flex-wrap gap-1 mt-auto">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${category.level === 0 ? 'bg-blue-50 text-blue-700 border border-blue-200' :
              category.level === 1 ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :
                category.level === 2 ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                  'bg-purple-50 text-purple-700 border border-purple-200'
              }`}>
              Level {category.level}
            </span>
            {category.parent && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                Sub-category
              </span>
            )}
            {category.itemCount > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                {category.itemCount} items
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryRow = (category) => {
    return (
      <tr key={category._id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
              {getCategoryIcon(category)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{category.name}</div>
              <div className="text-xs text-gray-500">{category.code}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <p className="text-xs text-gray-600 max-w-xs truncate">
            {category.description || '-'}
          </p>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${category.level === 0 ? 'bg-blue-100 text-blue-800' :
            category.level === 1 ? 'bg-indigo-100 text-indigo-800' :
              category.level === 2 ? 'bg-yellow-100 text-yellow-800' :
                'bg-purple-100 text-purple-800'
            }`}>
            Level {category.level}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <span className="text-xs text-gray-900">{category.itemCount || 0}</span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => handleViewCategory(category)}
              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-all"
              title="View details"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>

            <PermissionGuard permission="INVENTORY:CATEGORIES:UPDATE">
              <button
                onClick={() => handleEditCategory(category)}
                className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                title="Edit category"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </PermissionGuard>

            <PermissionGuard permission="INVENTORY:CATEGORIES:DELETE">
              <button
                onClick={() => {
                  clearError();
                  setShowDeleteConfirm(category);
                }}
                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                title="Delete category"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </PermissionGuard>
          </div>
        </td>
      </tr>
    );
  };

  if (loading.categories && categories.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200">

      {/* Form Modal */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {/* View Modal */}
      {viewingCategory && (
        <CategoryViewForm
          category={viewingCategory}
          onClose={() => setViewingCategory(null)}
          onEdit={handleEditCategory}
        />
      )}

      {/* Main Content */}
      {!showForm && (
        <>
          {/* Filters and Search */}
          <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-col lg:flex-row lg:items-center gap-2">
              {/* Search */}
              <div className="flex-1">
                <TextInput
                  variant="input"
                  type="text"
                  placeholder="Search categories by name, code, or description..."
                  value={searchTerm}
                  onChange={handleSearch}
                  icon={({ className }) => (
                    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                  className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                />
              </div>

              {/* Filters and Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                {/* Level Filter */}
                <CustomSelect
                  value={levelFilter}
                  onChange={handleLevelFilter}
                  options={[
                    { value: 'all', label: 'All Levels' },
                    { value: '0', label: 'Level 0 (Root)' },
                    { value: '1', label: 'Level 1' },
                    { value: '2', label: 'Level 2' },
                    { value: '3', label: 'Level 3+' }
                  ]}
                  size="sm"
                  variant="default"
                  className="min-w-[140px]"
                />

                {/* View Toggle */}
                <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                  <button
                    onClick={() => handleViewChange('card')}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    title="Card view"
                  >
                    <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    Cards
                  </button>
                  <button
                    onClick={() => handleViewChange('list')}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    title="List view"
                  >
                    <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    List
                  </button>
                </div>

                {/* Add Category Button */}
                <PermissionGuard permission="INVENTORY:CATEGORIES:CREATE">
                  <button
                    onClick={handleCreateCategory}
                    className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                  >
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Category
                  </button>
                </PermissionGuard>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {filteredCategories.length > 0 && (
            <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-gray-100 to-gray-100 border-b border-gray-200">
              {/* Results Info */}
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-900">
                    {startIndex + 1}–{Math.min(endIndex, totalItems)} of {totalItems} categories
                    {totalPages > 1 && (
                      <span className="text-[10px] text-gray-500 ml-2">
                        (Page {currentPage} of {totalPages})
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    {searchTerm || levelFilter !== 'all' ? 'Filtered results' : 'All categories'}
                  </p>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center gap-3">
                {/* Items per page */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Show:</span>
                  <CustomSelect
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                    disabled={loading?.categories}
                    options={[
                      { value: 10, label: '10' },
                      { value: 20, label: '20' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' }
                    ]}
                    size="sm"
                    variant="minimal"
                    className="min-w-[70px]"
                  />
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage <= 1 || loading?.categories}
                    className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="First page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1 || loading?.categories}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="hidden sm:inline">Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="hidden md:flex items-center gap-1">
                    {(() => {
                      const getPageNumbers = () => {
                        const pages = [];
                        const maxVisible = 7;
                        const halfVisible = Math.floor(maxVisible / 2);

                        if (totalPages <= maxVisible) {
                          // Show all pages if total pages is less than max visible
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Always show first page
                          pages.push(1);

                          let start = Math.max(2, currentPage - halfVisible);
                          let end = Math.min(totalPages - 1, currentPage + halfVisible);

                          // Adjust if we're near the beginning
                          if (currentPage <= halfVisible + 1) {
                            end = Math.min(totalPages - 1, maxVisible - 1);
                          }

                          // Adjust if we're near the end
                          if (currentPage >= totalPages - halfVisible) {
                            start = Math.max(2, totalPages - maxVisible + 2);
                          }

                          // Add ellipsis after first page if needed
                          if (start > 2) {
                            pages.push('...');
                          }

                          // Add middle pages
                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }

                          // Add ellipsis before last page if needed
                          if (end < totalPages - 1) {
                            pages.push('...');
                          }

                          // Always show last page if more than 1 page
                          if (totalPages > 1) {
                            pages.push(totalPages);
                          }
                        }

                        return pages;
                      };

                      return getPageNumbers().map((pageNum, index) => {
                        if (pageNum === '...') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-400 text-sm">
                              ...
                            </span>
                          );
                        }

                        const isCurrentPage = pageNum === currentPage;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading?.categories}
                            className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  {/* Mobile page indicator */}
                  <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                    <span className="text-sm font-medium text-gray-900">{currentPage}</span>
                    <span className="text-sm text-gray-500 mx-1">of</span>
                    <span className="text-sm font-medium text-gray-900">{totalPages}</span>
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages || loading?.categories}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages || loading?.categories}
                    className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Last page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Content */}
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No categories found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || levelFilter !== 'all' ?
                    'Try adjusting your search or filter criteria.' :
                    'Get started by creating your first category.'
                  }
                </p>
                {!searchTerm && levelFilter === 'all' && (
                  <div className="mt-6">
                    <PermissionGuard permission="INVENTORY:CATEGORIES:CREATE">
                      <button
                        onClick={handleCreateCategory}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                      >
                        <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Category
                      </button>
                    </PermissionGuard>
                  </div>
                )}
              </div>
            ) : (
              <>

                {/* Card View */}
                {view === 'card' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {paginatedCategories.map(renderCategoryCard)}
                  </div>
                )}

                {/* List View */}
                {view === 'list' && (
                  <div className="overflow-x-auto rounded-sm border border-gray-200">
                    <table className="min-w-max w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Category
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Level
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Items
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedCategories.map(renderCategoryRow)}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              

              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Category</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(null);
                    clearError();
                  }}
                  className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteCategory(showDeleteConfirm)}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
                >
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
